import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { X, FileText, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { RoleChip } from '@/components/shared/role-chip'
import { supabase } from '@/lib/supabase/client'
import { cn } from '@/lib/utils/cn'
import type { Member } from '../api/members-api'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

async function fetchMemberActivity(userId: string, projectId: string) {
  const [createdRes, auditRes] = await Promise.all([
    supabase
      .from('records')
      .select('id', { count: 'exact', head: true })
      .eq('project_id', projectId)
      .eq('created_by', userId),
    supabase
      .from('audit_entries')
      .select('id, event_type, description, created_at')
      .eq('project_id', projectId)
      .eq('actor_user_id', userId)
      .order('created_at', { ascending: false })
      .limit(8),
  ])
  return {
    recordsCreated: createdRes.count ?? 0,
    recentAudit: auditRes.data ?? [],
  }
}

const EVENT_LABELS: Record<string, string> = {
  record_created: 'ایجاد سابقه',
  record_status_changed: 'تغییر وضعیت',
  submission_submitted: 'ارسال فرم',
  comment_added: 'افزودن نظر',
}

interface MemberDetailPanelProps {
  member: Member
  projectId: string
  onClose: () => void
  onChangeRole: () => void
  onRemove: () => void
}

export function MemberDetailPanel({
  member,
  projectId,
  onClose,
  onChangeRole,
  onRemove,
}: MemberDetailPanelProps) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(id)
  }, [])

  const { data, isLoading } = useQuery({
    queryKey: ['member-activity', member.id, projectId],
    queryFn: () => fetchMemberActivity(member.userId, projectId),
  })

  return (
    <>
      <div className="fixed inset-0 z-30 bg-black/40" onClick={onClose} />
    <div className={cn(
      'fixed inset-y-0 left-0 w-80 bg-card border-r border-border shadow-xl z-40 flex flex-col',
      'transition-transform duration-300 ease-in-out',
      visible ? 'translate-x-0' : '-translate-x-full'
    )}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <h2 className="text-sm font-semibold text-foreground">اطلاعات عضو</h2>
        <Button variant="ghost" size="sm" className="w-7 h-7 p-0" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Member info */}
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-sm font-semibold text-primary shrink-0">
            {member.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">{member.name}</p>
            <p className="text-xs text-muted-foreground">{member.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <RoleChip role={member.role} />
          {member.joinedAt && (
            <span className="text-xs text-muted-foreground">
              عضو از {dayjs(member.joinedAt).fromNow()}
            </span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="px-5 py-4 border-b border-border">
        {isLoading ? (
          <div className="space-y-2">
            <div className="h-10 bg-muted rounded animate-pulse" />
            <div className="h-10 bg-muted rounded animate-pulse" />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-muted/30 rounded-lg p-3 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <FileText className="w-4 h-4 text-muted-foreground" />
              </div>
              <p className="text-lg font-bold text-foreground font-mono">
                {(data?.recordsCreated ?? 0).toLocaleString('fa-IR')}
              </p>
              <p className="text-xs text-muted-foreground">سابقه ایجاد شده</p>
            </div>
            <div className="bg-muted/30 rounded-lg p-3 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Clock className="w-4 h-4 text-muted-foreground" />
              </div>
              <p className="text-lg font-bold text-foreground font-mono">
                {(data?.recentAudit.length ?? 0).toLocaleString('fa-IR')}
              </p>
              <p className="text-xs text-muted-foreground">فعالیت اخیر</p>
            </div>
          </div>
        )}
      </div>

      {/* Recent activity */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">فعالیت اخیر</p>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-8 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : data?.recentAudit.length === 0 ? (
          <p className="text-xs text-muted-foreground">فعالیتی ثبت نشده</p>
        ) : (
          <div className="space-y-2">
            {data?.recentAudit.map((entry) => (
              <div key={entry.id} className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                <div>
                  <p className="text-xs text-foreground">
                    {EVENT_LABELS[entry.event_type] ?? entry.event_type}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {dayjs(entry.created_at).fromNow()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-5 py-4 border-t border-border space-y-2">
        <Button variant="outline" size="sm" className="w-full" onClick={onChangeRole}>
          تغییر نقش
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="w-full text-destructive border-destructive/40 hover:bg-destructive/10"
          onClick={onRemove}
        >
          حذف از پروژه
        </Button>
      </div>
    </div>
    </>
  )
}
